import {AxiosInstance, AxiosResponse} from "axios";
import LegacyAccess, {Access} from "@/core/interfaces/Access";
import Rank from "@/core/interfaces/Rank";

export interface CreateAccessParams {
    first_name: string;
    last_name: string;
    rankId?: number;
    operatorCode?: string;
}

export interface UpdateAccessParams extends Omit<LegacyAccess, 'userId'> {
    userId: number | null;
}

/**
 * The selects cannot hold a nullable value and legacy rows mix two empty
 * representations, so the resource is the single place that maps the
 * literal "null" and "" sentinels to a real null, on read and on write.
 */
const normalizeOperatorCode = (code?: string | null): string | null =>
    !code || code === 'null' ? null : code;


/**
 * The team resource.
 *
 * @param {Object} client - The injected client.
 * @returns {Object} The public object.
 */
export default function (client: AxiosInstance) {
    return {
        /**
         * Index of team members.
         *
         * @return {Promise}
         */
        index(): Promise<{ team: LegacyAccess[] }> {
            return client.get('/access/team')
                .then(response => ({
                    ...response.data,
                    team: (response.data.team ?? []).map((member: LegacyAccess) => ({
                        ...member,
                        operatorCode: normalizeOperatorCode(member.operatorCode)
                    }))
                }));
        },
        /**
         * Index of team members that have a prefillers status.
         *
         * @return {Promise}
         */
        async prefillers(): Promise<Access[]> {
            return await client.get('/access/prefillers')
                .then(response => response.data.data);
        },

        /**
         * Get operator codes.
         *
         * @returns {Promise}
         */
        operatorCodes(): Promise<string[]> {
            return client.get('/access/operators')
                .then(response => response.data.operators);
        },

        /**
         * Get rank list.
         *
         * @return {Promise<Rank[]>}
         */
        rankList(): Promise<Rank[]> {
            return client.get("/access/rank-list")
                .then((response: AxiosResponse) => response.data.list);
        },
        /**
         * Update access.
         *
         * @param {object} data - New access information.
         *
         * @returns {Promise}
         */
        updateAccess(data: UpdateAccessParams): Promise<any> {
            return client.put("/access/update", {...data, operatorCode: normalizeOperatorCode(data.operatorCode)})
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Create access.
         *
         * @param {object} data - New access information.
         *
         * @returns {Promise}
         */
        createAccess(data: CreateAccessParams): Promise<any> {
            return client.post("/access", {...data, operatorCode: normalizeOperatorCode(data.operatorCode)})
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Delete a user access.
         *
         * @param {number} userAccessId - The user access id.
         *
         * @return {Promise<any>}
         */
        deleteAccess(userAccessId: number): Promise<any> {
            return client.delete(`/access/${userAccessId}`)
                .then((response: AxiosResponse) => response.data);
        }
    };
}
